import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { getUploadHistory } from "@/api/files.api";
import { PageContainer } from "@components/PageContainer";
import { TableHeaderCell } from "@components/TableHeaderCell";
import { formatFileSize, formatTableDate } from "@/utils/format";
import { EMPTY_CELL_PLACEHOLDER } from "@/constants/app";
import type { UploadHistoryEntry } from "@/types";

export default function UploadHistoryPage() {
  const { t } = useTranslation();
  const [rows, setRows] = useState<UploadHistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getUploadHistory(1, 100)
      .then((res) => {
        if (!cancelled) setRows(res.data);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <PageContainer>
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h5"
          sx={{
            fontWeight: "bold",
          }}
        >
          {t("uploadHistory.title")}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
            mt: 0.5,
          }}
        >
          {t("uploadHistory.subtitle")}
        </Typography>
      </Box>
      <Card>
        {loading ? (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <Typography
              sx={{
                color: "text.secondary",
              }}
            >
              {t("common.loading")}
            </Typography>
          </Box>
        ) : rows.length === 0 ? (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <Typography
              sx={{
                color: "text.secondary",
              }}
            >
              {t("uploadHistory.empty")}
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>{t("uploadHistory.ref")}</TableHeaderCell>
                  <TableHeaderCell>{t("uploadHistory.type")}</TableHeaderCell>
                  <TableHeaderCell>{t("uploadHistory.extension")}</TableHeaderCell>
                  <TableHeaderCell>{t("files.size")}</TableHeaderCell>
                  <TableHeaderCell>{t("uploadHistory.uploaded")}</TableHeaderCell>
                  <TableHeaderCell>{t("uploadHistory.removed")}</TableHeaderCell>
                  <TableHeaderCell>{t("files.status")}</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => {
                  const isActive = row.uploadedFileId != null;
                  const status = isActive
                    ? { label: t("uploadHistory.statusActive"), color: "#10B981", bg: "#ECFDF5" }
                    : {
                        label: t("uploadHistory.statusRemoved"),
                        color: "#6B7280",
                        bg: "#F3F4F6",
                      };
                  return (
                    <TableRow key={row.id} hover>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: "monospace",
                            fontSize: 13,
                          }}
                        >
                          {row.id.slice(0, 8)}...
                        </Typography>
                      </TableCell>
                      <TableCell>{row.mimeCategory}</TableCell>
                      <TableCell>
                        {row.extension ? (
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: "monospace",
                            }}
                          >
                            .{row.extension}
                          </Typography>
                        ) : (
                          <Typography
                            variant="body2"
                            sx={{
                              color: "text.secondary",
                            }}
                          >
                            {EMPTY_CELL_PLACEHOLDER}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{formatFileSize(Number(row.sizeBytes))}</TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "text.secondary",
                          }}
                        >
                          {formatTableDate(row.uploadedAt)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {row.removedAt ? (
                          <Typography
                            variant="body2"
                            sx={{
                              color: "text.secondary",
                            }}
                          >
                            {formatTableDate(row.removedAt)}
                          </Typography>
                        ) : (
                          <Typography
                            variant="body2"
                            sx={{
                              color: "text.secondary",
                            }}
                          >
                            {EMPTY_CELL_PLACEHOLDER}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={status.label}
                          size="small"
                          sx={{
                            bgcolor: status.bg,
                            color: status.color,
                            fontWeight: 600,
                            fontSize: 12,
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>
    </PageContainer>
  );
}
